import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { theme } from "@/theme/theme";
import { useApiMutation } from "../../hooks/use-api-mutation";
import { useApiQuery } from "../../hooks/use-api-query";
import { useAutoScroll } from "../../hooks/use-auto-scroll";
import type { TestRun, TestRunStep } from "../../types";
import { LiveView } from "../live-view";
import { RunStatusChip } from "../run-status-chip";
import { TestRunStepCard } from "../test-run-step-card";

interface TestRunPanelProps {
  runId: string | null;
  testName?: string;
  open: boolean;
  onClose: () => void;
}

export function TestRunPanel({ runId, testName, open, onClose }: TestRunPanelProps) {
  const { enqueueSnackbar } = useSnackbar();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stepsContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: run,
    isLoading: isRunLoading,
    refetch,
  } = useApiQuery<TestRun>(`/vibe-qa/test-runs/${runId}`, {
    enabled: !!runId && open,
    refetchInterval: (query) => {
      const status = query.state.data?.run_status;
      if (
        status === "passed" ||
        status === "failed" ||
        status === "error" ||
        status === "cancelled"
      ) {
        return false;
      }
      return 3000;
    },
  });

  const { data: steps, isLoading: isStepsLoading } = useApiQuery<TestRunStep[]>(
    `/vibe-qa/test-runs/${runId}/steps`,
    {
      enabled: !!runId && open,
      refetchInterval: () => {
        const status = run?.run_status;
        if (
          status === "passed" ||
          status === "failed" ||
          status === "error" ||
          status === "cancelled"
        ) {
          return 5000;
        }
        return 1000;
      },
    },
  );

  // Auto-scroll for steps
  useAutoScroll(stepsContainerRef.current, steps?.length);

  const { mutate: cancelRun, isPending: isCancelling } = useApiMutation(
    "POST",
    `/vibe-qa/test-runs/${runId}/cancel`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test run cancelled", {
          variant: "warning",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(`Error cancelling test run: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  const isRunning = run?.run_status === "running" || run?.run_status === "pending";
  const status = run?.final_run_status || run?.run_status;

  const handleStepClick = (step: TestRunStep) => {
    if (!run?.recording_path || !videoRef.current) return;

    const runCreated = new Date(run.created_at).getTime();
    const stepCreated = new Date(step.created_at).getTime();
    const videoTime = (stepCreated - runCreated) / 1_000;

    const video = videoRef.current;
    if (!video.paused) {
      video.pause();
    }
    if (video.readyState >= 1) {
      video.currentTime = videoTime;
    } else {
      video.addEventListener(
        "loadedmetadata",
        () => {
          video.currentTime = videoTime;
        },
        { once: true },
      );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 580, lg: 700 },
          maxWidth: "100%",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
              {status && <RunStatusChip status={status} />}
              <Typography variant="h6" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {testName || run?.name || "Test Run"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {isRunning && (
                <Tooltip title="Cancel Test Run">
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    disabled={!run?.external_id || isCancelling}
                    onClick={() => cancelRun({})}
                  >
                    {isCancelling ? "Cancelling..." : "Cancel"}
                  </Button>
                </Tooltip>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {/* Liveview / Recording - Fixed height */}
        <Box sx={{ p: 2, flexShrink: 0 }}>
          {isRunning && run?.live_view_url ? (
            <LiveView
              liveViewUrl={run.live_view_url}
              height="320px"
              noLiveViewMessage="Waiting for live view..."
            />
          ) : run?.recording_path ? (
            <video
              ref={videoRef}
              controls
              autoPlay
              muted
              style={{
                width: "100%",
                height: "320px",
                objectFit: "contain",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: "common.black",
              }}
            >
              <source
                src={run.recording_path}
                type={`video/${run.recording_path.split("?")[0].split(".").pop()}`}
              />
              Your browser does not support the video tag.
            </video>
          ) : isRunLoading ? (
            <Box
              sx={{
                height: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "grey.100",
                borderRadius: "8px",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : (
            <Alert severity="info">
              {isRunning
                ? "Waiting for live view to become available..."
                : "No recording available for this run."}
            </Alert>
          )}
        </Box>

        <Divider sx={{ flexShrink: 0 }} />

        {/* Steps Header - Sticky */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle2">
            Test Steps {steps && steps.length > 0 && `(${steps.length})`}
          </Typography>
        </Box>

        {/* Steps - Scrollable */}
        <Box ref={stepsContainerRef} sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {isStepsLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Loading steps...
              </Typography>
            </Box>
          ) : !steps || steps.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No steps recorded for this run.
              </Typography>
            </Box>
          ) : (
            <Stack direction="column" spacing={2} alignItems="stretch">
              {steps.map((step) => (
                <TestRunStepCard
                  key={step.id + (step.code || "") + (step.updated_at || "")}
                  step={step}
                  onClick={run?.recording_path ? () => handleStepClick(step) : undefined}
                  isClickable={!!run?.recording_path}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
